import React from 'react';
import { makeStyles } from 'tss-react/mui';

import AnonSVG from 'ha/img-src/static/anon.svg';
import { units } from '@hbf/dsl';
import { Imgix } from 'ha/modules/Image/Imgix';
import { constants } from 'ha/constants/commonConstants';
import { Img } from 'ha/ui/Img';
import { useConfig } from 'ha/helpers/config';

const useStyles = makeStyles()(theme => ({
  avatarWrapper: {
    overflow: 'hidden',
    display: 'block',
    position: 'relative',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.common.white,
    backgroundPosition: '50%',
    backgroundSize: 'cover',
    borderRadius: '50%',
  },
  'avatar-lg': {
    width: units.rem(10),
    height: units.rem(10),
  },
  'avatar-md': {
    width: units.rem(7),
    height: units.rem(7),
  },
  'avatar-sm': {
    width: units.rem(5),
    height: units.rem(5),
  },
  'avatar-xs': {
    width: units.rem(3.6),
    height: units.rem(3.6),
  },
  'avatar-xxs': {
    width: units.rem(2.75),
    height: units.rem(2.75),
  },
  'avatar-xxxs': {
    width: units.rem(2),
    height: units.rem(2),
  },
  borderOutline: {
    border: units.border(1, 'solid', theme.palette.grey[100]),
  },
  activityStatus: {
    position: 'relative',

    '&:before': {
      content: '""',
      border: units.border(2, 'solid', theme.palette.common.white),
      borderRadius: '50%',
      position: 'absolute',
      zIndex: 2,
    },
  },
  'activityStatus-lg': {
    '&:before': {
      width: '18px',
      height: '18px',
      bottom: '9%',
      right: '9%',
    },
  },
  'activityStatus-md': {
    '&:before': {
      width: '16px',
      height: '16px',
      bottom: '7%',
      right: '7%',
    },
  },
  'activityStatus-sm': {
    '&:before': {
      width: '15px',
      height: '15px',
      bottom: '6%',
      right: '6%',
    },
  },
  'activityStatus-xs': {
    '&:before': {
      width: '10px',
      height: '10px',
      bottom: '6%',
      right: '6%',
    },
  },
  'activityStatus-xxs': {
    '&:before': {
      width: '9px',
      height: '9px',
      bottom: '5%',
      right: '5%',
    },
  },
  'activityStatus-xxxs': {
    '&:before': {
      width: '8px',
      height: '8px',
      bottom: '4%',
      right: '4%',
    },
  },
  statusOnline: {
    '&:before': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  statusOffline: {
    '&:before': {
      backgroundColor: theme.palette.grey[800],
    },
  },
}));

interface Props {
  avatarURL: string;
  isBordered?: boolean;
  className?: string;
  online?: boolean;
  offline?: boolean;
  imgClassName?: string;
  size?: 'lg' | 'md' | 'sm' | 'xs' | 'xxs' | 'xxxs';
}

export const UserAvatar: React.FC<Props> = ({
  avatarURL,
  online,
  offline,
  className,
  isBordered,
  imgClassName,
  size = 'lg',
}) => {
  const { classes, cx } = useStyles();

  const appConfig = useConfig();
  const imageTimeoutMs =
    appConfig && appConfig.thumb && appConfig.thumb.imageTimeoutMs;
  const showOnline = online === true;
  const showOffline =
    offline === true || (offline !== false && online === false);
  const hasAvatar = React.useMemo(
    () => avatarURL && !avatarURL.includes(constants.defaultAvatarUrl),
    [avatarURL],
  );

  return (
    <div
      className={cx(
        classes.avatarWrapper,
        className,
        classes[`avatar-${size}`],
        {
          [classes.activityStatus]: showOnline || showOffline,
          [classes[`activityStatus-${size}`]]: showOnline || showOffline,
          [classes.statusOnline]: showOnline,
          [classes.statusOffline]: showOffline,
        },
      )}
      data-test-locator="User Card Avatar"
    >
      {!hasAvatar && (
        <Img
          src={AnonSVG}
          defaultSrc={AnonSVG}
          alt="User avatar"
          className={cx(
            classes.avatarImg,
            classes[`avatar-${size}`],
            imgClassName,
            {
              [classes.borderOutline]: isBordered,
            },
          )}
          timeoutMs={imageTimeoutMs}
        />
      )}

      {hasAvatar && (
        <Imgix
          className={cx(
            classes.avatarImg,
            classes[`avatar-${size}`],
            imgClassName,
            {
              [classes.borderOutline]: isBordered,
            },
          )}
          height={160}
          htmlAttributes={{ alt: 'User avatar' }}
          imgixParams={{ fit: 'crop', mask: 'ellipse' }}
          src={avatarURL}
          width={160}
          fallbackSrc={AnonSVG}
        />
      )}
    </div>
  );
};
